import React from 'react'
import { useQuery } from '@apollo/react-hooks'
import { debounce } from 'lodash'
import { useRouter } from 'next/router'

import { useUser } from '~/hooks'
import { useSecureModalManager } from '~/hooks/use-modal-manager'
import Loader from '~/truck/loader'
import { cloudinary, queryUpdater } from '~/utils'

import UpsertReviewModal from '../../components/upsert-review-modal'
import productReviewsQuery from '../../gql/queries/product-reviews'

import { useTextContent } from './ProductReviews.utils'
import ProductReviewsView from './ProductReviews.view'

interface ProductReviewsProps {
  productSlug: string
}

const ProductReviews = (props: ProductReviewsProps) => {
  const { productSlug } = props

  const router = useRouter()

  const { open: openReviewModal } = useSecureModalManager(UpsertReviewModal)

  const { id: userId, isLoggedIn } = useUser()

  const { loading, data, fetchMore } = useQuery(productReviewsQuery, {
    variables: {
      slug: productSlug,
      page: 1,
      limit: 5,
    },
  })

  const product = data?.vendor?.product

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const loadMore = React.useCallback(
    debounce(
      page =>
        fetchMore({
          updateQuery: queryUpdater,
          variables: {
            page,
          },
        }),
      500,
    ),
    [fetchMore],
  )

  const pageInfo = data?.vendor?.product?.reviews?.pageInfo

  const reviewStats = data?.vendor?.product?.reviewStats ?? {}

  const url = cloudinary.getFullUrl('v1588089460/sellers-panel/empty_rating.png')

  const imageUrl = cloudinary.applyTransformations(url)

  const productReviews = data?.vendor?.product?.reviews?.items ?? []

  let upsertInitialValues = {}

  if (productReviews.length > 0) {
    if (productReviews[0].reviewer.id === userId) {
      upsertInitialValues = { ...productReviews[0] }
    }
  }

  const reviewButtonContent = useTextContent({
    isLoggedIn,
    router,
    onStartReview: () => {
      openReviewModal({ product, initialValues: upsertInitialValues })
    },
  })

  if (loading) {
    return <Loader centered style={{ width: '100%' }} />
  }

  return (
    <ProductReviewsView
      reviewButtonContent={reviewButtonContent}
      imageUrl={imageUrl}
      reviewStats={reviewStats}
      pageInfo={pageInfo}
      loadMore={loadMore}
      productReviews={productReviews}
    />
  )
}

export default ProductReviews
